import { memo, type ReactNode } from 'react'
import type { Check } from '@/lib/websocket'
import { StatusDot } from '@/components/StatusDot'
import { Badge } from '@/components/ui/badge'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { relativeTime } from '@/lib/utils'
import { cn } from '@/lib/utils'

/** Render function type for extra badges displayed in a CheckRow */
export type ExtraBadgeRenderer = (check: Check) => ReactNode

/** Render function for a trailing action slot (e.g. quick-test button) */
export type TrailingActionRenderer = (check: Check) => ReactNode

export interface CheckRowProps {
  check: Check
  isSelected: boolean
  isExpanded: boolean
  onSelect: () => void

  // --- Customization props ---
  /** Extra badges rendered after the type badge */
  extraBadges?: ExtraBadgeRenderer
  /** Trailing action rendered before the expand chevron */
  trailingAction?: TrailingActionRenderer
}

function getTypeBadgeVariant(type: string): 'info' | 'database' | 'warning' | 'secondary' {
  if (type.includes('pgsql') || type.includes('mysql')) return 'database'
  if (type === 'http') return 'info'
  if (type === 'icmp') return 'warning'
  return 'secondary'
}

export const CheckRow = memo(function CheckRow({
  check,
  isSelected,
  isExpanded,
  onSelect,
  extraBadges,
  trailingAction,
}: CheckRowProps) {
  const hasPartialSilence = !check.IsSilenced && check.SilencedChannels && check.SilencedChannels.length > 0

  const neverRun = !check.LastExec || check.LastExec === '0001-01-01T00:00:00Z'
  const statusText = !check.Enabled
    ? 'Disabled'
    : check.IsSilenced
      ? 'Silenced'
      : neverRun
        ? 'Pending'
        : check.LastResult
          ? 'Healthy'
          : 'FAILING'

  const statusColorClass = !check.Enabled
    ? 'text-disabled'
    : check.IsSilenced
      ? 'text-warning'
      : neverRun
        ? 'text-muted-foreground'
        : check.LastResult
          ? 'text-healthy'
          : 'text-unhealthy font-semibold'

  const target = check.URL || check.Host || '\u2014'

  return (
    <div
      id={`check-${check.UUID}`}
      role="button"
      tabIndex={0}
      className={cn(
        'cursor-pointer transition-colors text-sm',
        'hover:bg-muted/50',
        isSelected && 'bg-muted ring-1 ring-info/30',
        !check.Enabled && 'opacity-60'
      )}
      onClick={onSelect}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSelect() }}
    >
      {/* Mobile layout (<sm): stacked card */}
      <div className="flex sm:hidden flex-col gap-1.5 px-3 py-2">
        <div className="flex items-center gap-2 min-w-0">
          <StatusDot
            healthy={neverRun ? undefined : check.LastResult}
            enabled={check.Enabled}
            silenced={check.IsSilenced}
            partiallySilenced={hasPartialSilence}
          />
          <span className="font-medium truncate flex-1 min-w-0">{check.Name}</span>
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
          )}
        </div>

        <div
          className="font-mono text-xs text-muted-foreground truncate"
          onDoubleClick={(e) => {
            e.stopPropagation()
            if (target !== '\u2014') navigator.clipboard.writeText(target)
          }}
        >
          {target}
        </div>

        <div className="flex items-center gap-2 flex-wrap text-[11px] font-mono">
          <Badge variant={getTypeBadgeVariant(check.CheckType)} className="text-[10px] px-1.5 py-0">
            {check.CheckType}
          </Badge>
          {extraBadges && extraBadges(check)}
          <span className={statusColorClass}>{statusText}</span>
          {check.Periodicity && (
            <span className="text-muted-foreground">· {check.Periodicity}</span>
          )}
          <span className="text-muted-foreground">· {relativeTime(check.LastExec)}</span>
          {trailingAction && (
            <span className="ml-auto" onClick={(e) => e.stopPropagation()} role="presentation">
              {trailingAction(check)}
            </span>
          )}
        </div>
      </div>

      {/* Desktop layout (sm+): original single-row flex */}
      <div className="hidden sm:flex items-center gap-3 px-3 py-1.5">
        {/* Status dot */}
        <StatusDot
          healthy={neverRun ? undefined : check.LastResult}
          enabled={check.Enabled}
          silenced={check.IsSilenced}
          partiallySilenced={hasPartialSilence}
        />

        {/* Name */}
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="font-medium truncate min-w-0 w-[180px] shrink-0">{check.Name}</span>
          </TooltipTrigger>
          <TooltipContent>
            <span className="font-mono text-xs">{check.UUID}</span>
          </TooltipContent>
        </Tooltip>

        {/* Type badge */}
        <Badge variant={getTypeBadgeVariant(check.CheckType)} className="shrink-0 text-[10px] px-1.5 py-0">
          {check.CheckType}
        </Badge>

        {/* Extra badges */}
        {extraBadges && extraBadges(check)}

        {/* Status text */}
        <span className={cn('font-mono text-xs w-[70px] shrink-0', statusColorClass)}>
          {statusText}
        </span>

        {/* Host / URL */}
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className="font-mono text-xs text-muted-foreground break-words min-w-0 flex-1 cursor-pointer hover:text-foreground transition-colors"
              onDoubleClick={(e) => {
                e.stopPropagation()
                if (target !== '\u2014') navigator.clipboard.writeText(target)
              }}
            >
              {target}
            </span>
          </TooltipTrigger>
          <TooltipContent>Double-click to copy</TooltipContent>
        </Tooltip>

        {/* Frequency */}
        <span className="font-mono text-xs text-muted-foreground w-[40px] shrink-0 text-right">
          {check.Periodicity || '\u2014'}
        </span>

        {/* Last run */}
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="font-mono text-xs text-muted-foreground w-[60px] shrink-0 text-right">
              {relativeTime(check.LastExec)}
            </span>
          </TooltipTrigger>
          <TooltipContent>{check.LastExec}</TooltipContent>
        </Tooltip>

        {/* Trailing action (e.g. quick test) */}
        {trailingAction && trailingAction(check)}

        {/* Expand indicator */}
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
        )}
      </div>
    </div>
  )
})
